import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/utils/formatting";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

// Define the structure based on change_log table
type ChangeLogEntry = {
  id: number;
  version_id: number;
  provider_id: number;
  region_id: number;
  entity_type: string;
  entity_id: number;
  field_name: string;
  action_type: 'CREATE' | 'UPDATE' | 'DELETE';
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  updated_at: string;
};

interface ChannelChangesTableProps {
  changes?: ChangeLogEntry[];
  isLoading: boolean;
}

// Plan color mapping function
const getPlanColorClass = (colorIndex: number): string => {
  const colors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500',
    'bg-pink-500', 'bg-indigo-500', 'bg-gray-500', 'bg-orange-500', 'bg-teal-500',
    'bg-cyan-500', 'bg-lime-500', 'bg-emerald-500', 'bg-sky-500', 'bg-violet-500',
    'bg-fuchsia-500', 'bg-rose-500', 'bg-amber-500', 'bg-stone-500', 'bg-neutral-500',
    'bg-zinc-500', 'bg-slate-500', 'bg-red-400', 'bg-blue-400', 'bg-green-400',
    'bg-yellow-400', 'bg-purple-400', 'bg-pink-400', 'bg-indigo-400', 'bg-gray-400'
  ];
  return colors[(colorIndex - 1) % colors.length] || 'bg-gray-400';
};

type PlanInfo = {
  name: string;
  colorIndex: number;
};

export function ChannelChangesTable({ changes, isLoading }: ChannelChangesTableProps) {
  const [channelNames, setChannelNames] = useState<Record<number, string>>({});
  const [categoryNames, setCategoryNames] = useState<Record<number, string>>({});
  const [channelPlans, setChannelPlans] = useState<Record<number, PlanInfo[]>>({});

  // Filter out is_duplicate and is_local fields and include channels, plan assignments, and category assignments
  const channelChanges = changes?.filter(change =>
    change.field_name !== 'is_duplicate' &&
    change.field_name !== 'is_local' &&
    (change.entity_type === 'channels' ||
     change.entity_type === 'channel_plans' ||
     change.entity_type === 'channel_categories')
  ) || [];

  // Get unique channel IDs
  const channelIds = [...new Set(channelChanges.map(c => c.entity_id))];

  // Get category IDs from category_id field changes
  const categoryIds = [...new Set(
    channelChanges
      .filter(c => c.field_name === 'category_id')
      .flatMap(c => [c.old_value, c.new_value])
      .filter(v => v && v !== 'null')
      .map(v => parseInt(v!))
  )];

  // Fetch channel names
  useEffect(() => {
    const fetchChannelNames = async () => {
      if (channelIds.length === 0) return;

      const { data, error } = await supabase
        .from('channels')
        .select('id, name')
        .in('id', channelIds);

      if (!error && data) {
        const nameMap = data.reduce((acc, channel) => {
          acc[channel.id] = channel.name;
          return acc;
        }, {} as Record<number, string>);
        setChannelNames(nameMap);
      }
    };

    fetchChannelNames();
  }, [channelIds.join(',')]);

  // Fetch category names
  useEffect(() => {
    const fetchCategoryNames = async () => {
      if (categoryIds.length === 0) return;

      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .in('id', categoryIds);

      if (!error && data) {
        const nameMap = data.reduce((acc, category) => {
          acc[category.id] = category.name;
          return acc;
        }, {} as Record<number, string>);
        setCategoryNames(nameMap);
      }
    };

    fetchCategoryNames();
  }, [categoryIds.join(',')]);

  // Fetch channel plans
  useEffect(() => {
    const fetchChannelPlans = async () => {
      if (channelIds.length === 0) return;

      const { data, error } = await supabase
        .from('channel_plans')
        .select(`
          channel_id,
          plans:plan_id (
            name,
            color_index
          )
        `)
        .in('channel_id', channelIds);

      if (!error && data) {
        const plansMap = (data as any[]).reduce((acc, cp) => {
          if (!acc[cp.channel_id]) {
            acc[cp.channel_id] = [];
          }
          if (cp.plans && cp.plans.name) {
            acc[cp.channel_id].push({
              name: cp.plans.name,
              colorIndex: cp.plans.color_index || 1
            });
          }
          return acc;
        }, {} as Record<number, PlanInfo[]>);
        setChannelPlans(plansMap);
      }
    };

    fetchChannelPlans();
  }, [channelIds.join(',')]);

  // Group changes by channel and then by actual action type
  const groupedByChannel = channelChanges.reduce((acc, change) => {
    const channelId = change.entity_id;

    if (!acc[channelId]) {
      acc[channelId] = {
        channelId,
        channelName: channelNames[channelId] || `Channel #${channelId}`,
        createFields: {},
        updateFields: {},
        deleteFields: {},
        createTimestamp: null,
        updateTimestamp: null,
        deleteTimestamp: null,
        latestTimestamp: change.created_at
      };
    }

    // Track latest timestamp for this channel
    if (new Date(change.created_at) > new Date(acc[channelId].latestTimestamp)) {
      acc[channelId].latestTimestamp = change.created_at;
    }

    // Categorize changes based on old_value and new_value
    if (change.old_value === null && change.new_value !== null) {
      // Field was created (added)
      acc[channelId].createFields[change.field_name] = {
        value: change.new_value
      };
      if (!acc[channelId].createTimestamp || new Date(change.created_at) > new Date(acc[channelId].createTimestamp!)) {
        acc[channelId].createTimestamp = change.created_at;
      }
    } else if (change.old_value !== null && change.new_value === null) {
      // Field was deleted (removed)
      acc[channelId].deleteFields[change.field_name] = {
        value: change.old_value
      };
      if (!acc[channelId].deleteTimestamp || new Date(change.created_at) > new Date(acc[channelId].deleteTimestamp!)) {
        acc[channelId].deleteTimestamp = change.created_at;
      }
    } else if (change.old_value !== null && change.new_value !== null) {
      // Field was updated
      if (!acc[channelId].updateFields[change.field_name]) {
        acc[channelId].updateFields[change.field_name] = {
          oldValue: change.old_value,
          newValue: change.new_value
        };
      } else {
        // If field was already updated, keep the original old value but update the new value
        acc[channelId].updateFields[change.field_name].newValue = change.new_value;
      }
      if (!acc[channelId].updateTimestamp || new Date(change.created_at) > new Date(acc[channelId].updateTimestamp!)) {
        acc[channelId].updateTimestamp = change.created_at;
      }
    }

    return acc;
  }, {} as Record<number, {
    channelId: number;
    channelName: string;
    createFields: Record<string, { value: string }>;
    updateFields: Record<string, { oldValue: string; newValue: string }>;
    deleteFields: Record<string, { value: string }>;
    createTimestamp: string | null;
    updateTimestamp: string | null;
    deleteTimestamp: string | null;
    latestTimestamp: string;
  }>);

  if (isLoading) return <div>Loading...</div>;

  // Convert to array and sort
  const sortedChannels = Object.values(groupedByChannel).sort((a, b) =>
    new Date(b.latestTimestamp).getTime() - new Date(a.latestTimestamp).getTime()
  );

  const renderFieldValue = (
    actionType: 'CREATE' | 'UPDATE' | 'DELETE',
    fieldName: string,
    fieldData: any
  ) => {
    if (!fieldData) return <span className="text-muted-foreground">-</span>;

    // Handle category_id specially to show category names
    if (fieldName === 'category_id') {
      if (actionType === 'CREATE') {
        const categoryName = fieldData.value ? categoryNames[parseInt(fieldData.value)] || fieldData.value : null;
        return (
          <div className="text-xs text-green-600 font-medium">
            {categoryName || 'null'}
          </div>
        );
      } else if (actionType === 'UPDATE') {
        const oldCategoryName = fieldData.oldValue ? categoryNames[parseInt(fieldData.oldValue)] || fieldData.oldValue : null;
        const newCategoryName = fieldData.newValue ? categoryNames[parseInt(fieldData.newValue)] || fieldData.newValue : null;
        return (
          <div className="space-y-1">
            <div className="text-xs text-red-600 line-through">
              {oldCategoryName || 'null'}
            </div>
            <div className="text-xs text-green-600 font-medium">
              {newCategoryName || 'null'}
            </div>
          </div>
        );
      } else if (actionType === 'DELETE') {
        const categoryName = fieldData.value ? categoryNames[parseInt(fieldData.value)] || fieldData.value : null;
        return (
          <div className="text-xs text-red-600 line-through">
            {categoryName || 'null'}
          </div>
        );
      }
    }

    // Regular field handling
    if (actionType === 'CREATE') {
      return (
        <div className="text-xs text-green-600 font-medium">
          {fieldData.value || 'null'}
        </div>
      );
    } else if (actionType === 'UPDATE') {
      return (
        <div className="space-y-1">
          <div className="text-xs text-red-600 line-through">
            {fieldData.oldValue || 'null'}
          </div>
          <div className="text-xs text-green-600 font-medium">
            {fieldData.newValue || 'null'}
          </div>
        </div>
      );
    } else if (actionType === 'DELETE') {
      return (
        <div className="text-xs text-red-600 line-through">
          {fieldData.value || 'null'}
        </div>
      );
    }

    return <span className="text-muted-foreground">-</span>;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">Channel Name</TableHead>
          <TableHead className="font-semibold">Action</TableHead>
          <TableHead className="font-semibold">Name</TableHead>
          <TableHead className="font-semibold">Price</TableHead>
          <TableHead className="font-semibold">Description</TableHead>
          <TableHead className="font-semibold">Category</TableHead>
          <TableHead className="font-semibold">Plans</TableHead>
          <TableHead className="font-semibold">Channel Number</TableHead>
          <TableHead className="font-semibold">Timestamp</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedChannels.map((channel) => {
          // Build array of actions to display
          const actions = [];

          // Add CREATE row if there are created fields
          if (Object.keys(channel.createFields).length > 0) {
            actions.push({
              type: 'CREATE' as const,
              fields: channel.createFields,
              timestamp: channel.createTimestamp
            });
          }

          // Add UPDATE row if there are updated fields
          if (Object.keys(channel.updateFields).length > 0) {
            actions.push({
              type: 'UPDATE' as const,
              fields: channel.updateFields,
              timestamp: channel.updateTimestamp
            });
          }

          // Add DELETE row if there are deleted fields
          if (Object.keys(channel.deleteFields).length > 0) {
            actions.push({
              type: 'DELETE' as const,
              fields: channel.deleteFields,
              timestamp: channel.deleteTimestamp
            });
          }

          if (actions.length === 0) return null;

          return actions.map((action, index) => {
            const isFirstRow = index === 0;

            return (
              <TableRow key={`${channel.channelId}-${action.type}`} className="hover:bg-muted/50">
                {/* Channel Name - merged cell for first row only */}
                {isFirstRow && (
                  <TableCell
                    rowSpan={actions.length}
                    className="font-medium align-top border-r"
                  >
                    <div className="font-semibold">{channel.channelName}</div>
                  </TableCell>
                )}

                {/* Action */}
                <TableCell>
                  <Badge className={
                    action.type === 'CREATE' ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" :
                    action.type === 'UPDATE' ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100" :
                    "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
                  }>
                    {action.type}
                  </Badge>
                </TableCell>

                {/* Name */}
                <TableCell>
                  {renderFieldValue(action.type, 'name', action.fields['name'])}
                </TableCell>

                {/* Price */}
                <TableCell>
                  {renderFieldValue(action.type, 'price', action.fields['price'])}
                </TableCell>

                {/* Description */}
                <TableCell>
                  {renderFieldValue(action.type, 'description', action.fields['description'])}
                </TableCell>

                {/* Category */}
                <TableCell>
                  {renderFieldValue(action.type, 'category_id', action.fields['category_id'])}
                </TableCell>

                {/* Plans */}
                <TableCell>
                  {isFirstRow ? (
                    channelPlans[channel.channelId]?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {channelPlans[channel.channelId].map((plan, index) => (
                          <div
                            key={index}
                            className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-medium shadow-sm ${getPlanColorClass(plan.colorIndex)}`}
                            title={plan.name}
                          >
                            {plan.name.charAt(0).toUpperCase()}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">No plans</span>
                    )
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </TableCell>

                {/* Channel Number */}
                <TableCell>
                  {renderFieldValue(action.type, 'channel_number', action.fields['channel_number'])}
                </TableCell>

                {/* Timestamp */}
                <TableCell>
                  <div className="text-xs">{action.timestamp ? formatDateTime(action.timestamp) : '-'}</div>
                </TableCell>
              </TableRow>
            );
          });
        })}
      </TableBody>
    </Table>
  );
}